import type { CatalogFilters, PriceOverride, SortDirection, SortKey } from '@/types/data';

export interface ShareableState {
  compareIds: string[];
  baselineId: string | null;
  category: CatalogFilters['category'];
  subtype: string | null;
  sortKey: SortKey;
  sortDirection: SortDirection;
  query: string;
  includePrices: boolean;
  priceOverrides?: Record<string, PriceOverride>;
}

const SORT_KEYS: SortKey[] = [
  'alcoholPer100Calories',
  'alcoholPerDollar',
  'name',
  'abv',
  'calories',
];

function isSortKey(v: string): v is SortKey {
  return SORT_KEYS.includes(v as SortKey);
}

/** Map retired sort keys from old share links / prefs. */
export function normalizeSortKey(v: string | null | undefined): SortKey | null {
  if (!v) return null;
  if (v === 'alcoholPer100CaloriesPerDollar') return 'alcoholPer100Calories';
  return isSortKey(v) ? v : null;
}

export function encodeShareQuery(state: Omit<ShareableState, 'priceOverrides' | 'includePrices'>): string {
  const params = new URLSearchParams();
  if (state.compareIds.length) params.set('c', state.compareIds.join(','));
  if (state.baselineId) params.set('b', state.baselineId);
  if (state.category !== 'all') params.set('cat', state.category);
  if (state.subtype) params.set('sub', state.subtype);
  if (state.sortKey !== 'alcoholPer100Calories') params.set('sort', state.sortKey);
  if (state.sortDirection !== 'desc') params.set('dir', state.sortDirection);
  if (state.query) params.set('q', state.query);
  return params.toString();
}

export function decodeShareQuery(search: string): Partial<ShareableState> {
  const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  const result: Partial<ShareableState> = {};
  const c = params.get('c');
  if (c) result.compareIds = c.split(',').filter(Boolean).slice(0, 4);
  const b = params.get('b');
  if (b) result.baselineId = b;
  const cat = params.get('cat');
  if (cat) result.category = cat as CatalogFilters['category'];
  const sub = params.get('sub');
  if (sub) result.subtype = sub;
  const sort = normalizeSortKey(params.get('sort'));
  if (sort) result.sortKey = sort;
  const dir = params.get('dir');
  if (dir === 'asc' || dir === 'desc') result.sortDirection = dir as SortDirection;
  const q = params.get('q');
  if (q) result.query = q;
  return result;
}

/** User prices only in URL fragment when explicitly opted in. */
export function encodePriceFragment(
  overrides: Record<string, PriceOverride>,
): string {
  const compact = Object.values(overrides).map((o) => ({
    id: o.productId,
    p: o.packagePriceUsd,
    n: o.containerCount,
    ml: o.containerMl,
    tax: o.includeTaxDeposit ? 1 : 0,
  }));
  return `prices=${encodeURIComponent(JSON.stringify(compact))}`;
}

export function decodePriceFragment(
  hash: string,
): Record<string, PriceOverride> | null {
  const raw = hash.startsWith('#') ? hash.slice(1) : hash;
  const params = new URLSearchParams(raw);
  const prices = params.get('prices');
  if (!prices) return null;
  try {
    const parsed = JSON.parse(prices) as Array<{
      id: string;
      p: number;
      n: number;
      ml: number;
      tax?: number;
    }>;
    if (!Array.isArray(parsed)) return null;
    const out: Record<string, PriceOverride> = {};
    for (const row of parsed) {
      if (
        row &&
        typeof row.id === 'string' &&
        typeof row.p === 'number' &&
        row.p > 0 &&
        Number.isInteger(row.n) &&
        row.n > 0 &&
        typeof row.ml === 'number' &&
        row.ml > 0
      ) {
        out[row.id] = {
          productId: row.id,
          packagePriceUsd: row.p,
          containerCount: row.n,
          containerMl: row.ml,
          includeTaxDeposit: Boolean(row.tax),
          updatedAt: new Date().toISOString(),
          version: 1,
        };
      }
    }
    return out;
  } catch {
    return null;
  }
}
